import React, { useState } from 'react';
import { motion } from 'framer-motion';
import TrainerLayout from '../../components/trainer/TrainerLayout';
import { Upload, FileText, Video, Link as LinkIcon, Trash2, Plus, X } from 'lucide-react';
import Button from '../../components/ui/Button';

const mockMaterials = [
  { id: 1, title: 'Week 1 Slides - AI Foundations', type: 'pdf', size: '2.4 MB', course: 'AI for Finance', uploaded: '2026-05-01' },
  { id: 2, title: 'Python Setup Guide', type: 'pdf', size: '1.1 MB', course: 'AI for Finance', uploaded: '2026-05-03' },
  { id: 3, title: 'Live Session Recording - Week 2', type: 'video', size: '450 MB', course: 'AI for Finance', uploaded: '2026-05-08' },
  { id: 4, title: 'RBI Circular Dataset', type: 'link', size: '-', course: 'AI for Finance', uploaded: '2026-05-10' },
];

export default function TrainerMaterials(): React.JSX.Element {
  const [materials, setMaterials] = useState(mockMaterials);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadForm, setUploadForm] = useState({ title: '', course: 'AI for Finance', type: 'pdf' });

  const handleUpload = () => {
    if (!uploadForm.title) return;
    setMaterials(prev => [...prev, {
      id: Date.now(),
      title: uploadForm.title,
      type: uploadForm.type,
      size: 'Pending',
      course: uploadForm.course,
      uploaded: new Date().toISOString().split('T')[0],
    }]);
    setUploadForm({ title: '', course: 'AI for Finance', type: 'pdf' });
    setShowUpload(false);
  };

  const deleteMaterial = (id: number) => {
    setMaterials(prev => prev.filter(m => m.id !== id));
  };

  const typeIcons: Record<string, React.ElementType> = {
    pdf: FileText,
    video: Video,
    link: LinkIcon,
  };

  const typeColors: Record<string, string> = {
    pdf: 'bg-red-50 text-red-600',
    video: 'bg-purple-50 text-purple-600',
    link: 'bg-amber-50 text-amber-500',
  };

  return (
    <TrainerLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Materials</h1>
            <p className="text-sm text-slate-500">Upload and manage course materials</p>
          </div>
          <Button onClick={() => setShowUpload(true)} leftIcon={<Plus className="w-4 h-4" />}>
            Upload Material
          </Button>
        </div>

        {showUpload && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl border border-slate-200 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900">Upload New Material</h3>
              <button onClick={() => setShowUpload(false)} className="p-1 rounded-lg hover:bg-slate-100">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Title</label>
                <input
                  type="text"
                  value={uploadForm.title}
                  onChange={e => setUploadForm({ ...uploadForm, title: e.target.value })}
                  placeholder="e.g. Week 3 Slides"
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Course</label>
                <select
                  value={uploadForm.course}
                  onChange={e => setUploadForm({ ...uploadForm, course: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none"
                >
                  <option>AI for Finance</option>
                  <option>Data Science for Banking</option>
                  <option>Python for FP&A</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Type</label>
                <select
                  value={uploadForm.type}
                  onChange={e => setUploadForm({ ...uploadForm, type: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none"
                >
                  <option value="pdf">PDF Document</option>
                  <option value="video">Video</option>
                  <option value="link">External Link</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={handleUpload}
                  className="w-full px-4 py-2.5 bg-amber-500 text-white rounded-xl text-sm font-bold hover:bg-amber-600 transition-colors"
                >
                  Upload
                </button>
              </div>
            </div>
          </motion.div>
        )}

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3">Material</th>
                  <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3">Course</th>
                  <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3">Size</th>
                  <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3">Uploaded</th>
                  <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {materials.map((m) => {
                  const Icon = typeIcons[m.type] || FileText;
                  return (
                    <tr key={m.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${typeColors[m.type] || 'bg-slate-100'}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <span className="text-sm font-semibold text-slate-900">{m.title}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-600">{m.course}</td>
                      <td className="px-5 py-4 text-sm text-slate-500">{m.size}</td>
                      <td className="px-5 py-4 text-sm text-slate-500">{m.uploaded}</td>
                      <td className="px-5 py-4">
                        <button
                          onClick={() => deleteMaterial(m.id)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </TrainerLayout>
  );
}
